import ContentReferencesParser from './parsers/ContentReferencesParser';
import { interactiveParsers } from './parsers/InteractiveParsers';
import MermaidParser from './parsers/MermaidParser';

/**
 * MessageContentParser - Sistema de Parseo Universal para Herramientas LLM
 *
 * Soporta los siguientes patrones de herramientas:
 *
 * 1. D3 Visualizations:
 *    - [text](url) donde url contiene /visualizations/d3/
 *    - [D3_VISUALIZATION:url:title]
 *
 * 2. Mermaid Diagrams:
 *    - [MERMAID_DIAGRAM]contenido[/MERMAID_DIAGRAM]
 *    - Se convierte automáticamente a ```mermaid code blocks
 *    - Aprovecha la infraestructura existente en Markdown.js
 *
 * El sistema es extensible para futuras herramientas del LLM.
 */
class MessageContentParser {
  constructor() {
    this.parsers = [];
    this.registerDefaultParsers();
  }

  /**
   * Registra los parsers por defecto
   */
  registerDefaultParsers() {
    // Registrar parsers interactivos
    interactiveParsers.forEach((parser) => {
      this.registerParser(parser);
    });

    // Registrar otros parsers
    this.registerParser(new MermaidParser());
    this.registerParser(new ContentReferencesParser());
  }

  /**
   * Registra un nuevo parser
   * @param {BaseParser} parser - Instancia del parser
   */
  registerParser(parser) {
    this.parsers.push(parser);
    // Ordenar por prioridad (mayor prioridad primero)
    this.parsers.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Procesa el contenido y retorna array de parts para renderizado
   * @param {string} content - Contenido a procesar
   * @returns {Array<Object>} Array de parts
   */
  parseContent(content) {
    if (!content) return [];

    // PASO 1: Aplicar conversiones de contenido
    let processedContent = content;
    for (const parser of this.parsers) {
      processedContent = parser.convertContent(processedContent);
    }

    // PASO 2: Extraer todos los matches
    const allMatches = [];
    for (const parser of this.parsers) {
      const matches = parser.extractMatches(processedContent);
      for (const match of matches) {
        match.parser = parser; // Referencia al parser que lo creó
        allMatches.push(match);
      }
    }

    // PASO 3: Ordenar matches por posición
    allMatches.sort((a, b) => a.index - b.index);

    // PASO 4: Construir array de parts
    const parts = [];
    let lastIndex = 0;

    for (const match of allMatches) {
      // Texto antes del match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: processedContent.slice(lastIndex, match.index),
        });
      }

      // Solo crear part si el parser tiene lógica específica de renderizado
      if (match.type !== 'mermaid-conversion') {
        parts.push(match.parser.createPart(match));
      }

      lastIndex = match.index + match.length;
    }

    // Texto restante
    if (lastIndex < processedContent.length) {
      parts.push({
        type: 'text',
        content: processedContent.slice(lastIndex),
      });
    }

    // Si no hay matches, retornar todo como texto
    if (parts.length === 0) {
      parts.push({
        type: 'text',
        content: processedContent,
      });
    }

    return parts;
  }

  /**
   * Obtiene información sobre los parsers registrados
   * @returns {Array<Object>} Información de parsers
   */
  getRegisteredParsers() {
    return this.parsers.map((parser) => ({
      name: parser.name,
      priority: parser.priority,
      patterns: parser.getPatterns().map((p) => p.toString()),
    }));
  }
}

// Instancia singleton para uso global
const messageContentParser = new MessageContentParser();

export default messageContentParser;
export { MessageContentParser };
